/**
 * Planned vs actual (5.1.1): planned vs observed duration by visit type, planned
 * vs actual drive, and the recommendation a person turns into configuration.
 * This is where recalibration starts — "planning constants recalibrate from your
 * data" with a human hand on it — and the evidence behind the points
 * disagreement in 1.2.6. Nothing auto-applies.
 */
import type { ConfigState, VisitTypeKind } from "@caredna-marlin/utils/homeHealthDemo/state";
import { OBSERVED_BY_KIND, OBSERVED_TRAILING_WEEKS, type ObservedVisitType } from "../fixtures/plannedVsActual";
import { ROUTINE_DRIVE_MINUTES } from "./virtualCensus";

export const RECALIBRATION_THRESHOLD = 0.1;

export interface PlannedVsActualRow {
  kind: VisitTypeKind;
  label: string;
  points: number;
  /** Their point table read as hours all-in: points × 60 min. */
  pointImpliedAllInMinutes: number;
  plannedInHomeMinutes: number;
  plannedDriveMinutes: number;
  observedInHomeMinutes: number;
  observedDriveMinutes: number;
  sampleSize: number;
  /** (observed − planned) / planned, in-home. */
  durationDeltaPct: number;
  driveDeltaPct: number;
  /** Suggested planned in-home minutes when the delta passes the threshold; undefined when planned holds. */
  recommendedInHomeMinutes?: number;
  recommendation: string;
  /** Observed all-in vs point-implied all-in — the points disagreement, as information only. */
  pointsGapPct: number;
}

function roundTo5(n: number): number {
  return Math.round(n / 5) * 5;
}

export function plannedDriveFor(config: ConfigState, kind: VisitTypeKind): number {
  return config.visitTypes[kind].isAssessment ? config.socSlot.driveMinutes : ROUTINE_DRIVE_MINUTES;
}

export function selectPlannedVsActual(config: ConfigState, observed: ObservedVisitType[] = OBSERVED_BY_KIND): { rows: PlannedVsActualRow[]; trailingWeeks: number; recommendations: number } {
  const rows: PlannedVsActualRow[] = [];
  for (const o of observed) {
    const vt = config.visitTypes[o.kind];
    if (!vt) continue;
    const plannedDrive = plannedDriveFor(config, o.kind);
    const durationDeltaPct = (o.observedInHomeMinutes - vt.plannedMinutes) / vt.plannedMinutes;
    const driveDeltaPct = (o.observedDriveMinutes - plannedDrive) / plannedDrive;
    const pointImplied = vt.points * 60;
    const observedAllIn = o.observedInHomeMinutes + o.observedDriveMinutes;
    const pointsGapPct = (observedAllIn - pointImplied) / pointImplied;
    const recommend = Math.abs(durationDeltaPct) >= RECALIBRATION_THRESHOLD;
    const recommendedInHomeMinutes = recommend ? roundTo5(o.observedInHomeMinutes) : undefined;
    let recommendation: string;
    if (recommend) recommendation = `Recommend planned ${vt.plannedMinutes} → ${recommendedInHomeMinutes} min (observed ${o.observedInHomeMinutes}, n=${o.sampleSize})`;
    else recommendation = `Planned holds (observed ${o.observedInHomeMinutes} vs ${vt.plannedMinutes} min, n=${o.sampleSize})`;
    if (Math.abs(driveDeltaPct) >= RECALIBRATION_THRESHOLD) recommendation += ` · drive assumption ${plannedDrive} → ${roundTo5(o.observedDriveMinutes)} min`;
    rows.push({
      kind: o.kind,
      label: vt.label,
      points: vt.points,
      pointImpliedAllInMinutes: pointImplied,
      plannedInHomeMinutes: vt.plannedMinutes,
      plannedDriveMinutes: plannedDrive,
      observedInHomeMinutes: o.observedInHomeMinutes,
      observedDriveMinutes: o.observedDriveMinutes,
      sampleSize: o.sampleSize,
      durationDeltaPct,
      driveDeltaPct,
      recommendedInHomeMinutes,
      recommendation,
      pointsGapPct,
    });
  }
  rows.sort((a, b) => Math.abs(b.pointsGapPct) - Math.abs(a.pointsGapPct));
  return { rows, trailingWeeks: OBSERVED_TRAILING_WEEKS, recommendations: rows.filter((r) => r.recommendedInHomeMinutes !== undefined || Math.abs(r.driveDeltaPct) >= RECALIBRATION_THRESHOLD).length };
}
